import * as React from 'react'
import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { GridDensity, GridState } from '@mui/x-data-grid-pro'
import { useAppDispatch } from '@user/store/hooks'
import { WWMXGrid } from '@user/components/xGrid'
import {
  defaultLeadColumns,
  lockedLeadColumns,
} from '@user/components/Leads/LeadList/leadListColumns'
import {
  IUserPreferenceGeneralListItemType,
  saveGeneralPreferences,
  userGeneralPreferencesList,
} from '@user/store/userPreferences/userPreferencesGeneral'
import { useGuestUserLeadsColumns } from './hooks/Lead/useGuestUserLeadsColumns'
import { useGuestUserLeadsActions } from './hooks/Lead/useGuestUserLeadsActions'
import {
  getDefaultGuestUserLeadsColumnVisibility,
  selectGuestUserLeads,
  selectGuestUserLeadsFilterSet,
  selectGuestUserLeadsLoading,
  selectGuestUserLeadsPage,
  selectGuestUserLeadsPerPage,
  selectGuestUserLeadsTotalCount,
  setLeadFilters,
  setOppsFilters,
} from '@user/store/guestUser/slice'
import Box from '@mui/material/Box'
import { Deposit } from '@workwave-tidal/core/illustrations/Deposit'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import AddIcon from '@mui/icons-material/Add'
import { useNavigate } from 'react-router-dom'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'
import SearchIcon from '@mui/icons-material/Search'
import { useSaveLeadFilters } from '@user/components/Dashboard/GuestUserDashboard/hooks/Lead/useSaveGuestUserLeadFilters'
import { orderByOptions } from '@user/store/lead'
import {
  defaultGuestUserLeadFilters,
  defaultGuestUserOpportunityFilters,
  guestUserClearedLeadFilters,
} from '@user/store/guestUser/filters'
import { validateLeadFilters } from '@user/components/Leads/LeadFilters/utils'
import { LeadFilters } from '@user/components/Leads/LeadFilters'

interface GuestUserLeadsTableProps {
  setFiltersDisabled: (state: boolean) => void
  coreDataLoading?: boolean
}

export function GuestUserLeadsTable(props: GuestUserLeadsTableProps) {
  const dispatch = useAppDispatch()
  const { columns, columnVisibility } = useGuestUserLeadsColumns()
  const navigate = useNavigate()

  const leads = useSelector(selectGuestUserLeads)
  const loading = useSelector(selectGuestUserLeadsLoading)
  const count = useSelector(selectGuestUserLeadsTotalCount)
  const page = useSelector(selectGuestUserLeadsPage)
  const perPage = useSelector(selectGuestUserLeadsPerPage)
  const filterSet = useSelector(selectGuestUserLeadsFilterSet)
  const saveLeadFilters = useSaveLeadFilters()
  const defaultVisibility = getDefaultGuestUserLeadsColumnVisibility()

  const {
    handlePaginationModelChange,
    sortModel,
    onSortModelChange,
    onSaveColumnState,
    selectionChangeCallback,
    onColumnWidthChange,
    handleSearchChange,
    searchText,
    onClickExport,
  } = useGuestUserLeadsActions({ ...props, columns })

  const userGeneralPreferences = useSelector(userGeneralPreferencesList)

  const densityModeUserGeneralPreference = userGeneralPreferences?.find(
    (row) =>
      row?.type ==
      IUserPreferenceGeneralListItemType.GuestUserDashboardLeadsDensityMode,
  )

  const stringValue = densityModeUserGeneralPreference?.value ?? 'standard'
  const densityMode = stringValue as GridDensity

  const onStateChange = (state: GridState) => {
    if (state.density) {
      onDensityChange(state.density.value)
    }
  }

  const onDensityChange = (density: string) => {
    if (densityMode != density) {
      dispatch(
        saveGeneralPreferences({
          type: IUserPreferenceGeneralListItemType.GuestUserDashboardLeadsDensityMode,
          value: density,
        }),
      )
    }
  }

  const onAddNewLead = useCallback(() => {
    dispatch(setLeadFilters(defaultGuestUserLeadFilters))
    dispatch(setOppsFilters(defaultGuestUserOpportunityFilters))
    navigate('/leads/addLead')
  }, [dispatch, navigate])

  return (
    <Box position="relative">
      <TextField
        sx={{ position: 'absolute', right: '20px', zIndex: 1000 }}
        onChange={handleSearchChange}
        value={searchText}
        placeholder="Search..."
        spellCheck={false}
        variant="standard"
        type="search"
        InputProps={{
          sx: {
            padding: (theme) => `${theme.spacing(0.9)} ${theme.spacing(1.2)}`,
            '&::after': { borderBottom: '1px solid #' },
            '&::before': { borderBottom: '1px solid #ffffff00' },
            borderBottom: 'none',
          },
          startAdornment: (
            <InputAdornment position="start">
              <IconButton size="small">
                <SearchIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <WWMXGrid
        loading={loading || props.coreDataLoading}
        applyCustomStyles={true}
        virtualScrollerHeight={
          densityMode === 'compact'
            ? 325
            : densityMode === 'standard'
              ? 342
              : 358
        }
        // columns
        columnVisibilityModel={columnVisibility}
        columns={columns}
        onColumnWidthChange={(e) =>
          onColumnWidthChange(e?.colDef, columns, columnVisibility)
        }
        columnsSelectorProps={{
          saveCallback: (e) => onSaveColumnState(e, columns),
          lockedColumns: lockedLeadColumns,
          defaultColumns: defaultLeadColumns,
          defaultVisibility: defaultVisibility,
        }}
        //state
        isDensityEnable
        isExportEnable
        density={densityMode}
        onStateChange={onStateChange}
        //export
        exportClickCallback={onClickExport}
        exportFileName="Leads"
        // rows
        rows={leads}
        rowCount={count}
        // pagination
        paginationModel={{ page: page - 1, pageSize: perPage }}
        onPaginationModelChange={handlePaginationModelChange}
        pageSizeOptions={[10, 25, 50]}
        // sorting
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        selectionChangeCallback={selectionChangeCallback}
        width="100%"
        // filter
        isFilterEnable
        gridFilterProps={{
          applyCallback: saveLeadFilters,
          clearedValues: guestUserClearedLeadFilters,
          defaultValues: defaultGuestUserLeadFilters,
          initialValues: filterSet,
          orderOptions: orderByOptions,
          hideOrderBy: true,
          hideCreatedBy: true,
          disabled: false,
          validate: validateLeadFilters,
          filterComponent: LeadFilters,
          appliedCount: filterSet?.appliedCount || 0,
        }}
        slots={{
          noRowsOverlay: () => (
            <Box
              style={{ backgroundColor: 'white', border: '1px solid #e0e0e0' }}
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              height="100%"
              textAlign="center"
              width={`calc(100vw - 87px)`}
              zIndex={1000}
            >
              <Deposit style={{ fontSize: '100px' }} />
              <Typography variant="h6" color="textSecondary" mt={2} mb={2}>
                No leads to display
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                sx={{ width: '174px' }}
                onClick={onAddNewLead}
                data-test-id="guestUserEmptyStateAddLeadBtn"
              >
                Add lead
              </Button>
            </Box>
          ),
        }}
      />
    </Box>
  )
}

GuestUserLeadsTable.displayName = 'GuestUserLeadsTable'
